/**
 * @fileoverview Utility functions for Chart.js components
 * @module components/ui/chart-utils
 */

import type { ChartData } from 'chart.js'

/**
 * Get theme-aware colors from CSS custom properties
 * Returns actual resolved color values that Chart.js can understand
 */
export function getThemeColors(): {
  positive: string
  negative: string
  neutral: string
  chartGrid: string
  chartSurface: string
  foreground: string
  muted: string
  background: string
  border: string

  /* Mockup-aligned series tokens */
  revenue: string
  adSpend: string
  cogs: string

  /* Chart text/line tokens */
  chartGridColor: string
  chartBorderColor: string
  chartTickColor: string
  chartPointBg: string
} {
  if (typeof window === 'undefined') {
    return {
      positive: 'oklch(0.55 0.12 160)',
      negative: 'oklch(0.55 0.18 25)',
      neutral: 'oklch(0.708 0 0)',
      chartGrid: 'oklch(0.922 0 0)',
      chartSurface: 'oklch(1 0 0)',
      foreground: 'oklch(0.145 0 0)',
      muted: 'oklch(0.556 0 0)',
      background: 'transparent',
      border: 'oklch(0.922 0 0)',

      // Fallbacks matching mockup palette
      revenue: '#8B5CF6',
      adSpend: '#F59E0B',
      cogs: '#A78BFA',

      chartGridColor: 'rgba(0, 0, 0, 0.10)',
      chartBorderColor: 'rgba(0, 0, 0, 0.20)',
      chartTickColor: 'rgba(0, 0, 0, 0.70)',
      chartPointBg: 'rgba(255, 255, 255, 0.90)',
    }
  }

  const cs = getComputedStyle(document.documentElement)
  const read = (name: string, fallback: string): string => {
    const v = cs.getPropertyValue(name).trim()
    return v || fallback
  }

  return {
    positive: read('--color-positive', 'oklch(0.55 0.12 160)'),
    negative: read('--color-negative', 'oklch(0.55 0.18 25)'),
    neutral: read('--color-neutral', 'oklch(0.708 0 0)'),
    chartGrid: read('--color-chart-grid', 'oklch(0.922 0 0)'),
    chartSurface: read('--color-chart-surface', 'oklch(1 0 0)'),
    foreground: read('--color-foreground', 'oklch(0.145 0 0)'),
    muted: read('--color-muted-foreground', 'oklch(0.556 0 0)'),
    background: 'transparent',
    border: read('--color-border', 'oklch(0.922 0 0)'),

    // Mockup-aligned series tokens (resolved)
    revenue: read('--color-chart-legend-revenue', read('--chart-legend-revenue', '#8B5CF6')),
    adSpend: read('--color-chart-legend-adspend', read('--chart-legend-adspend', '#F59E0B')),
    cogs: read('--color-chart-legend-cogs', read('--chart-legend-cogs', '#A78BFA')),

    // Chart text/line tokens (resolved)
    chartGridColor: read(
      '--color-chart-grid-color',
      read('--chart-grid-color', 'rgba(0,0,0,0.10)')
    ),
    chartBorderColor: read(
      '--color-chart-border-color',
      read('--chart-border-color', 'rgba(0,0,0,0.20)')
    ),
    chartTickColor: read(
      '--color-chart-tick-color',
      read('--chart-tick-color', 'rgba(0,0,0,0.70)')
    ),
    chartPointBg: read(
      '--color-chart-point-bg',
      read('--chart-point-bg', 'rgba(255,255,255,0.90)')
    ),
  }
}

/**
 * Utility function to create area chart data with fill
 *
 * Default opacity rationale:
 * - We use a slightly lower default area fill opacity (0.30, down from 0.40) to balance
 *   readability in light/dark themes and reduce “checkerboard/white cell” artifacts when
 *   multiple semi‑transparent stacked areas overlap over a transparent canvas with visible gridlines.
 * - Individual charts can still override via dataset.fillOpacity when they need stronger emphasis.
 */
export function createAreaData(
  labels: string[],
  datasets: Array<{
    label: string
    data: number[]
    color: string
    fillOpacity?: number
    // Optional stack id to support stacked areas
    stack?: string
    // Optional drawing order (higher values draw first/behind, lower values draw last/on top)
    order?: number
  }>,
  options?: {
    // When true, render as pure line (no area fill) globally.
    disableFill?: boolean
    // Fill only the target area below the line down to the x-axis (origin).
    // This is equivalent to Chart.js { fill: 'origin' } behavior.
    fillBelowLineOnly?: boolean
  }
): ChartData<'line', number[], string> {
  const disableFill = options?.disableFill === true
  const fillBelow = options?.fillBelowLineOnly === true

  return {
    labels,
    datasets: datasets.map(dataset => ({
      label: dataset.label,
      data: dataset.data,
      borderColor: dataset.color,
      // If fill is disabled, keep background fully transparent.
      backgroundColor: disableFill
        ? 'transparent'
        : `color-mix(in oklab, ${dataset.color} ${(dataset.fillOpacity ?? 0.3) * 100}%, transparent)`,
      // Fill behavior:
      // - disabled: false
      // - below-only: 'origin' (down to x-axis baseline)
      // - default: true (classic area)
      fill: disableFill ? false : fillBelow ? 'origin' : true,
      tension: 0.4,
      borderWidth: 2,
      // Make points transparent by default and show only a hover ring
      pointRadius: 0,
      pointHoverRadius: 8,
      pointBorderWidth: 2,
      pointBackgroundColor: 'transparent',
      // Pass through stack id for Chart.js stacking
      ...(dataset.stack ? { stack: dataset.stack } : {}),
      // Pass through order for Chart.js drawing order
      ...(dataset.order !== undefined ? { order: dataset.order } : {}),
    })),
  }
}

/**
 * Utility function to format currency values for charts
 */
import { formatMoneyCompact } from '@/shared/utils/money'
export function formatChartCurrency(value: number): string {
  // Generic fallback used in rare cases where currency context is unavailable
  return formatMoneyCompact(value, 'USD', 'en-US')
}

/**
 * Get donut chart color scheme as CSS variable references
 * Returns CSS variable references that can be used in className
 */
export function getDonutChartColors(): {
  cogs: string
  adSpend: string
  profit: string
  overhead: string
  meta: string
  google: string
  influencers: string
} {
  return {
    cogs: 'var(--chart-legend-cogs)',
    adSpend: 'var(--chart-legend-adspend)',
    profit: 'var(--chart-legend-profit)',
    overhead: 'var(--chart-legend-overhead, var(--chart-legend-cogs))',
    meta: 'var(--chart-legend-meta)',
    google: 'var(--chart-legend-google)',
    influencers: 'var(--chart-legend-influencers)',
  }
}

/**
 * Get resolved color values for Chart.js (which needs actual color values)
 */
export function getResolvedDonutChartColors(): {
  cogs: string
  adSpend: string
  profit: string
  meta: string
  google: string
  influencers: string
} {
  if (typeof window === 'undefined') {
    return {
      cogs: '#A78BFA',
      adSpend: '#F59E0B',
      profit: '#10B981',
      meta: '#6366F1',
      google: '#3B82F6',
      influencers: '#EC4899',
    }
  }

  const cs = getComputedStyle(document.documentElement)
  const read = (name: string, fallback: string): string => {
    const v = cs.getPropertyValue(name).trim()
    return v || fallback
  }

  return {
    cogs: read('--chart-legend-cogs', '#A78BFA'),
    adSpend: read('--chart-legend-adspend', '#F59E0B'),
    profit: read('--chart-legend-profit', '#10B981'),
    meta: read('--chart-legend-meta', '#6366F1'),
    google: read('--chart-legend-google', '#3B82F6'),
    influencers: read('--chart-legend-influencers', '#EC4899'),
  }
}

/**
 * Return semantic text token var for a numeric delta.
 * Positive or zero -> --text-positive; Negative -> --text-negative
 */
export function getTrendTokenColor(delta: number): string {
  return delta >= 0 ? 'var(--text-positive)' : 'var(--text-negative)'
}

/**
 * Return semantic border token var for a numeric delta.
 * Positive or zero -> --border-positive; Negative -> --border-negative
 */
export function getTrendBorderToken(delta: number): string {
  return delta >= 0 ? 'var(--border-positive)' : 'var(--border-negative)'
}

/**
 * Format percentage values for display
 */
export function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`
}
